import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { useFirestoreQueryListener } from 'lib/frontend/hooks/useFirestoreQueryListener';
import { cdnIfy } from 'lib/helpers';
import { OrderModel } from 'lib/model/objects/orderModel';
import { dbToUICurrencyString, formatPriceWithCents } from 'lib/pricing/ui';
import { safeAsync, safeGetOrThrow } from 'lib/safeWrappers';
import { ESnapshotExists, Event } from 'lib/types';
import {
  ORDER_CANCELLED,
  ORDER_EDITED,
  ORDER_INVOICE_CREATED,
  ORDER_INVOICE_PAID,
  ORDER_INVOICE_PAID_OUT_OF_PLATFORM,
  ORDER_INVOICE_PAYMENT_NOTE,
  ORDER_INVOICE_REFUNDED,
  ORDER_MANUAL_SYNC_REQUEST,
  ORDER_NEWSPAPER_ORDER_CONFIRMED,
  ORDER_OBITUARY_VERIFIED,
  OrderEvent
} from 'lib/types/events';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import moment from 'moment';
import { selectActiveOrganization, selectIsPublisher } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { getFirebaseContext } from 'utils/firebase';
import { getBooleanFlag } from 'utils/flags';
import { getLogger } from 'utils/logger';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { CLASSIFIED_ROUTES } from 'router/routes';
import { useAppParams } from 'lib/frontend/utils/router';
import { useFirestoreDocumentListener } from 'lib/frontend/hooks/useFirestoreDocumentListener';
import { getModelFromSnapshot } from 'lib/model';
import { filterAsync } from 'lib/helpers/array/async';

type SerializedEvent = {
  label: string;
  description?: React.ReactNode;
};

type OrderActivityLogEventProps = {
  serializedEvent: SerializedEvent;
  timestamp: Event['createdAt'];
};
function OrderActivityLogEvent({
  serializedEvent,
  timestamp
}: OrderActivityLogEventProps) {
  const { description, label } = serializedEvent;
  const activeOrganization = useAppSelector(selectActiveOrganization);

  return (
    <div className="pt-6 pb-8 flex justify-between gap-2">
      <div className="text-sm">
        <div className="font-medium">{label}</div>
        {description && (
          <div className="text-column-gray-400">{description}</div>
        )}
      </div>
      <div className="text-xs text-column-gray-400 w-56 text-right">
        {moment(timestamp.toMillis())
          .tz(activeOrganization?.data()?.iana_timezone || 'America/Chicago')
          .format("D MMM 'YY [at] LT z")}
      </div>
    </div>
  );
}

const serializeOrderEvent = async (
  orderEvent: ESnapshotExists<OrderEvent>,
  isPublisher: boolean
): Promise<ResponseOrError<SerializedEvent | null>> => {
  const orderEventData = orderEvent.data();
  switch (orderEventData.type) {
    case ORDER_INVOICE_PAID:
    case ORDER_INVOICE_PAID_OUT_OF_PLATFORM: {
      const {
        data: { invoice }
      } = orderEventData;

      const [invoiceSnapshotError, invoiceSnapshot] = await safeGetOrThrow(
        invoice
      );
      if (invoiceSnapshotError) {
        return wrapError(invoiceSnapshotError);
      }

      const useColumnCDN = getBooleanFlag(LaunchDarklyFlags.ENABLE_COLUMN_CDN);
      const amountPaid = invoiceSnapshot.data().amount_paid;
      const receiptUrl = invoiceSnapshot.data().receipt_pdf;
      const isIOC = invoiceSnapshot.data().invoiceOutsideColumn === true;
      if (isIOC) {
        return wrapSuccess({
          label: 'Order is invoiced outside of Column',
          description:
            'Please reach out to the publisher directly to pay for this order. Do not send payment to Column.'
        });
      }
      return wrapSuccess({
        label: isPublisher ? 'Invoice paid by advertiser' : 'Invoice paid',
        description: (
          <>
            {invoiceSnapshot.data().customer_email} paid the invoice for $
            {dbToUICurrencyString(amountPaid || 0)}.
            {receiptUrl && (
              <>
                {' View '}
                <a
                  className="underline"
                  href={cdnIfy(receiptUrl, {
                    useColumnCDN
                  })}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  receipt here
                </a>
              </>
            )}
          </>
        )
      });
    }
    case ORDER_EDITED: {
      const {
        data: { editedBy }
      } = orderEventData;
      if (!editedBy) {
        return wrapSuccess({
          label: 'Order edited',
          description: ``
        });
      }
      const [editorError, editor] = await safeGetOrThrow(editedBy);
      if (editorError) {
        return wrapError(editorError);
      }
      if (isPublisher) return wrapSuccess(null);
      return wrapSuccess({
        label: 'Order edited',
        description: `Edited by ${editor.data().email}`
      });
    }
    case ORDER_INVOICE_CREATED: {
      const {
        data: { orderInvoice }
      } = orderEventData;
      const [invoiceSnapshotError, invoiceSnapshot] = await safeGetOrThrow(
        orderInvoice
      );
      await safeGetOrThrow(orderInvoice);
      if (invoiceSnapshotError) {
        return wrapError(invoiceSnapshotError);
      }
      return wrapSuccess({
        label: isPublisher
          ? 'Invoice sent to advertiser'
          : 'Invoice created by publisher',
        description: (
          <>
            {invoiceSnapshot.data().customer_email} received an invoice
            {!isPublisher && (
              <>
                {' '}
                for $
                {dbToUICurrencyString(
                  invoiceSnapshot.data().pricing?.totalInCents || 0
                )}
              </>
            )}
            .
          </>
        )
      });
    }
    case ORDER_INVOICE_REFUNDED: {
      const {
        data: { refundAmount, refundedBy, refundReason }
      } = orderEventData;
      const [refunderError, refunder] = refundedBy
        ? await safeGetOrThrow(refundedBy)
        : [null, null];
      if (refunderError) {
        return wrapError(refunderError);
      }
      const refundedByEmailPart = refunder
        ? ` by ${refunder.data().email}`
        : '';
      const formattedAmountWithSymbol = formatPriceWithCents('$', refundAmount);
      const refundedByNamePart = refunder ? ` by ${refunder.data().name}` : '';
      const reasonPart = refundReason ? ` Reason: ${refundReason}` : '';
      return wrapSuccess({
        label: `Order refunded${refundedByEmailPart}`,
        description: `Order refunded for ${formattedAmountWithSymbol}${refundedByNamePart}.${reasonPart}`
      });
    }
    case ORDER_CANCELLED: {
      const {
        data: { cancelledByEmail, cancellationReason }
      } = orderEventData;
      return wrapSuccess({
        label: `Order cancelled by ${cancelledByEmail}`,
        description: cancellationReason
      });
    }
    case ORDER_OBITUARY_VERIFIED: {
      const { data: verificationData } = orderEventData;

      if (verificationData.verificationMethod === 'automatic') {
        return wrapSuccess({
          label: 'Order verified automatically'
        });
      }
      const { verifiedBy } = verificationData;
      let verifiedByEmail: string;
      if (typeof verifiedBy === 'string') {
        verifiedByEmail = verifiedBy;
      } else {
        const [verifierError, verifier] = await safeGetOrThrow(verifiedBy);
        if (verifierError) {
          return wrapError(verifierError);
        }
        verifiedByEmail = verifier.data().email;
      }
      return wrapSuccess({
        label: 'Order verified manually',
        description: `Verified by ${verifiedByEmail}`
      });
    }
    case ORDER_NEWSPAPER_ORDER_CONFIRMED: {
      const {
        data: { confirmedBy, newspaperOrder }
      } = orderEventData;
      const [confirmerError, confirmer] = await safeGetOrThrow(confirmedBy);
      if (confirmerError) {
        return wrapError(confirmerError);
      }
      const [newspaperOrderSnapshotError, newspaperOrderSnapshot] =
        await safeGetOrThrow(newspaperOrder);
      if (newspaperOrderSnapshotError) {
        return wrapError(newspaperOrderSnapshotError);
      }
      const [newspaperError, newspaper] = await safeGetOrThrow(
        newspaperOrderSnapshot.data().newspaper
      );
      if (newspaperError) {
        return wrapError(newspaperError);
      }
      return wrapSuccess({
        label: `Confirmed for ${newspaper.data().name}`,
        description: `Order confirmed by ${confirmer.data().email}`
      });
    }
    case ORDER_INVOICE_PAYMENT_NOTE: {
      const {
        data: { note }
      } = orderEventData;

      return wrapSuccess({
        label: isPublisher
          ? 'Advertiser paid invoice by cash or check'
          : 'Invoice paid',
        description: `Recorded with payment note: ${note}`
      });
    }
    case ORDER_MANUAL_SYNC_REQUEST: {
      const {
        data: { requestedBy }
      } = orderEventData;
      const [requestedByError, requestedBySnapshot] = await safeGetOrThrow(
        requestedBy
      );
      if (requestedByError) {
        return wrapError(requestedByError);
      }

      return wrapSuccess({
        label: 'Manual sync requested',
        description: `Requested by ${requestedBySnapshot.data().email}`
      });
    }
    default: {
      return wrapSuccess(null);
    }
  }
};

type ParsedEvent = {
  serializedEvent: SerializedEvent;
  id: string;
  timestamp: Event['createdAt'];
};
const generateSyntheticEvents = async (
  order: OrderModel,
  isPublisher: boolean
): Promise<ResponseOrError<ParsedEvent[]>> => {
  const [newspaperOrdersError, newspaperOrders] = await safeAsync(() =>
    order.getNewspaperOrders()
  )();
  if (newspaperOrdersError) return wrapError(newspaperOrdersError);
  const newspaperResponses = await Promise.all(
    newspaperOrders.map(newspaperOrder =>
      safeGetOrThrow(newspaperOrder.modelData.newspaper)
    )
  );
  const newspapers = [];
  for (const [pullNewspaperError, newspaper] of newspaperResponses) {
    if (pullNewspaperError) return wrapError(pullNewspaperError);
    newspapers.push(newspaper);
  }

  return wrapSuccess([
    {
      serializedEvent: {
        label: isPublisher
          ? 'Order received'
          : `Order submitted to ${newspapers
              .map(newspaper => newspaper.data().name)
              .join(', ')}`
      },
      timestamp: order.modelData.createdAt,
      id: 'order-submitted'
    }
  ]);
};

function OrderActivityLog() {
  const { id: orderId } = useAppParams(CLASSIFIED_ROUTES.DETAIL);
  const isPublisher = useAppSelector(selectIsPublisher);

  const orderRef = getFirebaseContext().ordersRef().doc(orderId);

  const orderSnap = useFirestoreDocumentListener(orderRef);

  const order = orderSnap
    ? getModelFromSnapshot(OrderModel, getFirebaseContext(), orderSnap)
    : null;

  const eventQuery = useFirestoreQueryListener(
    getFirebaseContext().eventsRef<OrderEvent>().where('order', '==', orderRef),
    [orderRef.id]
  );

  const { value: serializedEvents, isLoading } = useAsyncEffect({
    fetchData: async () => {
      if (!order || !eventQuery) return;
      const [serializationError, serializedEvents] = await filterAsync(
        eventQuery.docs,
        async event => {
          const [error, serializedEvent] = await serializeOrderEvent(
            event,
            isPublisher
          );
          if (error) {
            getLogger().info('Failed to serialize event', {
              eventId: event.id
            });
            return wrapError(error);
          }
          if (!serializedEvent) {
            return wrapSuccess(null);
          }
          return wrapSuccess({
            serializedEvent,
            timestamp: event.data().createdAt,
            id: event.id
          });
        }
      );
      if (serializationError) {
        getLogger().warn('Unable to serialize events for order activity log');
        return [];
      }
      const [syntheticEventsError, syntheticEvents] =
        await generateSyntheticEvents(order, isPublisher);
      if (syntheticEventsError) {
        getLogger().warn(
          'Unable to generate synthetic events for order activity log'
        );
        return [];
      }
      return serializedEvents
        .concat(syntheticEvents)
        .sort((a, b) =>
          a.timestamp.toMillis() > b.timestamp.toMillis() ? 1 : -1
        );
    },
    dependencies: [eventQuery?.docs.map(d => d.id).join(',')]
  });

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col">
        <div className="w-full flex-1">
          {isLoading || !serializedEvents ? (
            <div className="pt-12">
              <LoadingSpinner />
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 px-6">
              {serializedEvents.map(({ serializedEvent, timestamp, id }) => (
                <OrderActivityLogEvent
                  serializedEvent={serializedEvent}
                  timestamp={timestamp}
                  key={id}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderActivityLog;
